<template>
  <div>
    <!-- Page Header Start -->
    <div class="row page-title-header">
      <div class="col-12">
        <BreadCrumb 
          parentRouteName="BookingPresetList" 
          :extra-items="isEditing ? [
            {
              text: bookingPreset.name,
              to: 'BookingCenterBookingList',
              params: $route.params.id
            },
            '編輯預約設定檔'
          ] : [
            '新增預約設定檔'
          ]" 
        />
      </div>
    </div>
    <!-- Page Header End -->

    <!-- Form Start -->
    <div class="email-wrapper wrapper">
      <div class="row bg-white">
        <div class="col-md-2 mail-sidebar pt-3">
          <StepNavigation
            ref="stepNavigation"
            :steps="navigationSteps"
            @changed="newStep => step = newStep.hash"
          />
        </div>
        <div class="col-md-10 main-view">
          <b-overlay :show="showLoading">
            <b-card>
              <!-- 基本資料 section start -->
              <section v-if="step == ''">
                <!-- 欄位部分 Start -->
                <div class="row d-flex mb-4 mb-xl-2">
                  <h4 class="col-12 col-xl-6 mb-3 font-weight-bold">
                    {{ isEditing ? "編輯預約設定檔" : "新增預約設定檔" }}
                  </h4>
                  <div class="col-12 col-xl-8">
                    <validation-error-alert
                      v-if="validationErrors"
                      :errors="validationErrors"
                    ></validation-error-alert>
                    <!-- 名稱 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="*名稱"
                    >
                      <b-form-input
                        class="mb-2 mr-sm-2 mb-sm-0"
                        type="text"
                        :state="v$.bookingPreset.name.$error ? false : null"
                        v-model="bookingPreset.name"
                      ></b-form-input>
                      <b-form-invalid-feedback
                        :state="!v$.bookingPreset.name.$error"
                      >
                        此欄位為必填
                      </b-form-invalid-feedback>
                    </b-form-group>
                    <!-- 名稱 End -->
                    <!-- 類型 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="*預約類型"
                    >
                      <b-form-radio-group
                        v-if="hasBookingPresetTypePermission"
                        v-model="bookingPreset.type"
                        :options="bookingPresetTypes"
                      ></b-form-radio-group>
                      <p v-else class="mt-1">
                        無權限
                      </p>
                    </b-form-group>
                    <!-- 類型 End -->
                    <!-- 預約說明 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="預約說明"
                    >
                      <b-form-textarea
                        class="mb-2 mr-sm-2 mb-sm-0"
                        v-model="bookingPreset.config.description"
                      ></b-form-textarea>
                    </b-form-group>
                    <!-- 預約說明 End -->
                    <!-- 預約狀態 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="預約狀態"
                    >
                      <OptionsEditor
                        v-model="statusConfigField.options"
                        :default-value.sync="bookingPreset.config.booking_mapping.default_status_value"
                        :header-is-visible="true"
                        :default-option-is-visible="true"
                      />
                    </b-form-group>
                    <!-- 預約狀態 End -->
                  </div>
                </div>
                <!-- 欄位部分 End -->
                <!-- 表單底部 Start -->
                <div class="d-flex justify-content-center">
                  <b-button
                    class="mr-3"
                    @click="cancel"
                    variant="outline-danger"
                  >
                    返回
                  </b-button>
                  <b-button :disabled="!hasBookingPresetTypePermission" @click="submit" variant="success"> 儲存 </b-button>
                </div>
                <!-- 表單底部 End -->
              </section>
              <!-- 基本資料 section end -->

              <!-- 日期時段 section start -->
              <section v-if="step == '#time-setting'">
                <validation-error-alert
                  v-if="validationErrors"
                  :errors="validationErrors"
                ></validation-error-alert>

                <b-form-group
                  label-cols="12"
                  label-cols-lg="2"
                  label-size="sm"
                  label="格式 *"
                >
                  <b-form-radio-group
                    class="input-group"
                    v-model="timeConfigField.format"
                    :options="[{ text: '日期 + 時間', value: 'datetime' }, { text: '日期', value: 'date' }, { text: '時間', value: 'time' }]"
                  ></b-form-radio-group>
                </b-form-group>

                <b-form-group
                  label-cols="12"
                  label-cols-lg="2"
                  label-size="sm"
                  label="日期格式 *"
                  v-if="(timeConfigField.format || '').includes('date')"
                >
                  <b-form-radio-group
                    v-model="timeConfigField.date_condition"
                    :options="[{ text: '自行選擇日期', value: 'unlimited' }, { text: '限定日期選項', value: 'limited' }, { text: '週期日期', value: 'periodic', disabled: true }]"
                  >
                  </b-form-radio-group>
                </b-form-group>

                <b-form-group
                  label-cols="12"
                  label-cols-lg="2"
                  label-size="sm"
                  label="日期呈現樣式"
                  v-if="(timeConfigField.format || '').includes('date')"
                >
                  <b-form-radio-group
                    v-model="timeConfigField.date_style"
                    :options="[{ text: '日曆', value: 'calendar_style' }, { text: '下拉選項', value: 'select_style' }]"
                  >
                  </b-form-radio-group>
                </b-form-group>

                <b-form-group
                  label-cols="12"
                  label-cols-lg="2"
                  label-size="sm"
                  label="選項 *"
                  v-if="(timeConfigField.format || '').includes('date') && timeConfigField.date_condition == 'limited'"
                >
                  <OptionsEditor
                    v-model="timeConfigField.options"
                    type="date"
                  />
                </b-form-group>

                <b-form-group
                  label-cols="12"
                  label-cols-lg="2"
                  label-size="sm"
                  label="時間格式 *"
                  v-if="(timeConfigField.format || '').includes('time')"
                >
                  <b-form-radio-group
                    class="input-group"
                    v-model="timeConfigField.time_condition"
                    :options="[
                      { text: '自行選擇時間', value: 'unlimited' },
                      { text: '限定時間選項', value: 'limited' }
                    ]"
                    @change="timeConfigField.time_condition === 'unlimited' ?
                        timeConfigField.time_style = 'time_select_style' :
                        timeConfigField.time_style = 'select_style'
                      "
                  ></b-form-radio-group>
                </b-form-group>

                <b-form-group
                  label-cols="12"
                  label-cols-lg="2"
                  label-size="sm"
                  label="時間呈現樣式"
                  v-if="(timeConfigField.format || '').includes('time')"
                >
                  <b-form-radio-group
                    v-model="timeConfigField.time_style"
                    :options="[
                      { text: '時間下拉', value: 'time_select_style', disabled: true },
                      { text: '自訂下拉', value: 'select_style', disabled: timeConfigField.time_condition === 'unlimited' },
                      { text: '按鈕選項', value: 'button_style', disabled: timeConfigField.time_condition === 'unlimited' }
                    ]"
                  >
                  </b-form-radio-group>
                </b-form-group>

                <template v-if="(timeConfigField.format || '').includes('time') && timeConfigField.time_condition == 'limited'">
                  <b-form-group
                    label-cols="12"
                    label-cols-lg="2"
                    label-size="sm"
                    label="開始時間 *"
                  >
                    <AppTimePicker
                      v-model="timeConfigField.start_time"
                    />
                  </b-form-group>

                  <b-form-group
                    label-cols="12"
                    label-cols-lg="2"
                    label-size="sm"
                    label="結束時間 *"
                  >
                    <AppTimePicker
                      v-model="timeConfigField.end_time"
                    />
                  </b-form-group>

                  <b-form-group
                    label-cols="12"
                    label-cols-lg="2"
                    label-size="sm"
                    label="每個時間區段單位 *"
                  >
                    <b-form-input
                      v-model="timeConfigField.slot_minutes"
                    ></b-form-input>
                  </b-form-group>
                </template>

                <!-- 預約時長 Start -->
                <b-form-group
                  label-cols="12"
                  label-cols-lg="2"
                  label-size="sm"
                  label="預約時長"
                >
                  <b-form-input
                    class="mb-2 mr-sm-2 mb-sm-0"
                    type="text"
                    v-model="bookingPreset.config.basic.duration_minutes"
                  ></b-form-input>
                </b-form-group>
                <!-- 預約時長 End -->

                <!-- 提前預約設定 Start -->
                <b-form-group
                  label-cols="12"
                  label-cols-lg="2"
                  label-size="sm"
                  label="提前預約設定"
                >
                  <label class="py-2 d-inline-flex align-items-center">
                    <b-form-radio name="lead-time-options" v-model="bookingPreset.config.basic.lead_time.type" value="none" />
                    無需提前預約
                  </label>
                  <br/>
                  <label class="py-2 d-inline-flex align-items-center">
                    <b-form-radio name="lead-time-options" v-model="bookingPreset.config.basic.lead_time.type" value="day" />
                    需提前
                    <b-form-input
                      :disabled="bookingPreset.config.basic.lead_time.type !== 'day'"
                      v-model="bookingPreset.config.basic.lead_time.amount_day"
                      type="number"
                      min="0"
                      class="d-inline-block mx-1"
                      style="width: 70px;"
                      size="sm"
                    ></b-form-input>
                    天預約
                  </label>
                  <br/>
                  <label class="py-2 d-inline-flex align-items-center">
                    <b-form-radio name="lead-time-options" v-model="bookingPreset.config.basic.lead_time.type" value="minute" />
                    需提前
                    <b-form-input
                      :disabled="bookingPreset.config.basic.lead_time.type !== 'minute'"
                      v-model="bookingPreset.config.basic.lead_time.amount_minute"
                      type="number"
                      min="0"
                      class="d-inline-block mx-1"
                      style="width: 70px;"
                      size="sm"
                    ></b-form-input>
                    分鐘預約
                  </label>
                </b-form-group>
                <!-- 提前預約設定 End -->

                <!-- 預約時間限制 Start -->
                <b-form-group
                  label-cols="12"
                  label-cols-lg="2"
                  label-size="sm"
                  label="*預約時間限制"
                >
                  <label class="py-2 d-inline-flex align-items-center">
                    <b-form-radio name="booking-window-options" v-model="bookingPreset.config.basic.booking_window.type" value="none" />
                    不限制
                  </label>
                  <br/>
                  <label class="py-2 d-inline-flex align-items-center">
                    <b-form-radio name="booking-window-options" v-model="bookingPreset.config.basic.booking_window.type" value="day" />
                    只能預約
                    <b-form-input
                      :disabled="bookingPreset.config.basic.booking_window.type !== 'day'"
                      v-model="bookingPreset.config.basic.booking_window.amount_day"
                      type="number"
                      min="0"
                      class="d-inline-block mx-1"
                      style="width: 70px;"
                      size="sm"
                    ></b-form-input>
                    天內的預約
                    <span id="booking-window-tooltip-trigger" class="ml-2"><span class="mdi mdi-help-circle-outline"></span></span>
                    <b-tooltip target="booking-window-tooltip-trigger" placement="top">
                      1 天代表當日；2 代表當日與隔日
                    </b-tooltip>
                  </label>
                  <br/>
                  <label class="py-2 d-inline-flex align-items-center">
                    <b-form-radio name="booking-window-options" v-model="bookingPreset.config.basic.booking_window.type" value="minute" />
                    只能預約未來
                    <b-form-input
                      :disabled="bookingPreset.config.basic.booking_window.type !== 'minute'"
                      v-model="bookingPreset.config.basic.booking_window.amount_minute"
                      type="number"
                      min="0"
                      class="d-inline-block mx-1"
                      style="width: 70px;"
                      size="sm"
                    ></b-form-input>
                    分鐘內的預約
                  </label>
                </b-form-group>
                <!-- 預約時間限制 End -->

                <div class="d-flex justify-content-center mt-2">
                  <b-button
                    class="mr-3"
                    @click="cancel"
                    variant="outline-danger"
                  >
                    返回
                  </b-button>
                  <b-button @click="submit" variant="success"> 儲存 </b-button>
                </div>
              </section>
              <!-- 日期時段 section end -->

              <!-- 分店時間表 section start -->
              <section v-if="step == '#business-hours'">
                <validation-error-alert
                  v-if="validationErrors"
                  :errors="validationErrors"
                ></validation-error-alert>

                <div class="reservation-periods-container">
                  <div class="row reservation-periods-header">
                    <div class="col-2"></div>
                    <div class="col-2 text-center">例行公休日</div>
                    <div class="col-3 text-center">預約開始時間</div>
                    <div class="col-3 text-center">最後預約時間</div>
                    <div class="col-2 text-center">當日休店時間</div>
                  </div>
                  <div
                    v-for="(period, dayIndex) in reservationPeriods"
                    :key="dayIndex"
                    class="row reservation-period-row align-items-center mb-2"
                  >
                    <div class="col-2">
                      <span>{{ period.display_text }}</span>
                    </div>
                    <div class="col-2 text-center">
                      <b-form-checkbox
                        v-model="period.is_official_holiday"
                        @change="updateReservationPeriod(dayIndex, 'is_official_holiday', $event)"
                      ></b-form-checkbox>
                    </div>
                    <div class="col-3">
                      <AppTimePicker
                        v-model="period.business_hours[0].start_time"
                        styleType="unlimited"
                        @input="updateReservationPeriod(dayIndex, 'start_time', $event)"
                        :disabled="period.is_official_holiday"
                      />
                      <!-- <b-form-invalid-feedback
                        :state="getValidationState(dayIndex, 'start_time', 'beforeEndTime')"
                      >
                        預約開始時間必須小於最後預約時間
                      </b-form-invalid-feedback> -->
                    </div>
                    <div class="col-3">
                      <AppTimePicker
                        v-model="period.business_hours[0].end_time"
                        styleType="unlimited"
                        @input="updateReservationPeriod(dayIndex, 'end_time', $event)"
                        :disabled="period.is_official_holiday"
                      />
                      <!-- <b-form-invalid-feedback
                        :state="getValidationState(dayIndex, 'end_time', 'beforeClosingTime')"
                      >
                        最後預約時間必須小於等於當日休店時間
                      </b-form-invalid-feedback> -->
                    </div>
                    <div class="col-2">
                      <AppTimePicker
                        v-model="period.closing_time"
                        styleType="unlimited"
                        @input="updateReservationPeriod(dayIndex, 'closing_time', $event)"
                        :disabled="period.is_official_holiday"
                      />
                    </div>
                  </div>
                </div>

                <div class="d-flex justify-content-center mt-2">
                  <b-button
                    class="mr-3"
                    @click="cancel"
                    variant="outline-danger"
                  >
                    返回
                  </b-button>
                  <b-button @click="submit" variant="success"> 儲存 </b-button>
                </div>
              </section>
              <!-- 分店時間表 section end -->

              <!-- 配額設定 section start -->
              <section v-if="step == '#limit-setting'">
                <NameBlock :label="'預約設定檔名稱'" :name="bookingPreset.name"/>

                <b-form-radio-group v-model="bookingPreset.config.occupy_config.limit_type" class="pt-3" @input="handleOccupanceLimitTypeChange">
                  <b-form-radio value="unlimited" class="form-inline w-100 mb-2">
                    不限定
                  </b-form-radio>
                  <div class="d-flex align-items-center w-100 mb-2">
                    <b-form-radio value="time" class="mr-0"></b-form-radio>
                    每時段固定
                    <input
                      class="form-control mx-2"
                      style="width: 80px"
                      type="text"
                      v-model="bookingPreset.config.occupy_config.slot_people"
                    />
                    人
                  </div>
                  <b-form-radio value="collection" class="form-inline w-100 mb-2" disabled>
                    C 商品[規格]：分店
                  </b-form-radio>
                </b-form-radio-group>

                <div class="d-flex justify-content-center mt-2">
                  <b-button
                    class="mr-3"
                    @click="cancel"
                    variant="outline-danger"
                  >
                    返回
                  </b-button>
                  <b-button @click="submit" variant="success"> 儲存 </b-button>
                </div>
              </section>
              <!-- 配額設定 section end -->

              <!-- 預約欄位 section start -->
              <section v-if="step == '#fields'">
                <NameBlock :label="'預約名稱'" :name="bookingPreset.name" />
                <div>
                  <DynamicFormEditor
                    @output="saveFields"
                    @cancel="$router.push({ name: 'BookingPresetList' })"
                    editor-title="欄位設定"
                    :form-type="'booking'"
                    :input-subjects="sortableFields"
                    :subject-is-fixed="subjectIsFixed"
                    :pages="bookingPreset.config.pages"
                    :sections="bookingPreset.config.sections"
                    :validate-subject="validSubject"
                    validation-message="標題不得為空"
                    enable-visible-condition
                  ></DynamicFormEditor>
                </div>
              </section>
              <!-- 預約欄位 section end -->

              <!-- 發布 section start -->
              <section v-if="step == '#publish'">
                <!-- 欄位部分 Start -->
                <div class="row d-flex mb-4 mb-xl-2">
                  <div class="col-12 col-xl-8">
                    <validation-error-alert
                      v-if="validationErrors"
                      :errors="validationErrors"
                    ></validation-error-alert>
                    <!-- 填寫權限 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="填寫權限"
                    >
                      <b-form-checkbox-group
                        v-model="bookingPreset.config.permissions"
                        :options="permissionOptions"
                      ></b-form-checkbox-group>
                    </b-form-group>
                    <!-- 填寫權限 End -->
                    <!-- 發布狀態 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="發布狀態"
                    >
                      <b-form-select
                        v-model="bookingPreset.status"
                      >
                        <b-form-select-option value="published">發布</b-form-select-option>
                        <b-form-select-option value="draft">草稿</b-form-select-option>
                        <b-form-select-option value="closed">關閉</b-form-select-option>
                      </b-form-select>
                    </b-form-group>
                    <!-- 發布狀態 End -->
                    <!-- 開始時間 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="* 開始時間"
                    >
                      <AppDateTimePicker
                        v-model="bookingPreset.start_at"
                        placeholderForDate="選擇日期"
                        placeholderForTime="選擇時間"
                        :has-error="v$.bookingPreset.start_at.$error"
                      />
                      <b-form-invalid-feedback :state="!v$.bookingPreset.start_at.$error">
                        此欄位為必填
                      </b-form-invalid-feedback>
                    </b-form-group>
                    <!-- 開始時間 End -->
                    <!-- 結束時間 Start -->
                    <b-form-group
                      label-cols="12"
                      label-cols-lg="2"
                      label-size="sm"
                      label="* 結束時間"
                    >
                      <AppDateTimePicker
                        v-model="bookingPreset.end_at"
                        placeholderForDate="選擇日期"
                        placeholderForTime="選擇時間"
                        :has-error="v$.bookingPreset.end_at.$error || v$.bookingPreset.start_at.beforeEndAt.$invalid"
                      />
                      <b-form-invalid-feedback :state="!v$.bookingPreset.end_at.$error">
                        此欄位為必填
                      </b-form-invalid-feedback>
                      <b-form-invalid-feedback
                        :state="!v$.bookingPreset.start_at.beforeEndAt.$invalid"
                      >
                        結束時間必須大於起始時間
                      </b-form-invalid-feedback>
                    </b-form-group>
                    <!-- 結束時間 End -->
                  </div>
                </div>
                <!-- 欄位部分 End -->
                <!-- 表單底部 Start -->
                <div class="d-flex justify-content-center">
                  <b-button
                    class="mr-3"
                    @click="cancel"
                    variant="outline-danger"
                  >
                    返回
                  </b-button>
                  <b-button :disabled="!hasBookingPresetTypePermission" @click="submit" variant="success"> 儲存 </b-button>
                </div>
                <!-- 表單底部 End -->
              </section>
              <!-- 發布 section end -->

            </b-card>
          </b-overlay>
        </div>
      </div>
    </div>
    <!-- Form End -->
  </div>
</template>

<script>
import { isBefore } from "date-fns"
import deepGet from "lodash/get"
import { mapGetters } from "vuex"
import useVuelidate from "@vuelidate/core"
import { required } from "@vuelidate/validators"
import bookingApi from "@/apis/booking"
import * as consts from "@/consts"
import AppDatePicker from '@/components/AppDatePicker.vue';
import AppTimePicker from '@/components/AppTimePicker.vue'
import AppDateTimePicker from '@/components/AppDateTimePicker.vue'
import OptionsEditor from '@/components/Dashboard/OptionsEditor.vue'
import StepNavigation from '@/components/Dashboard/StepNavigation.vue'
import DynamicFormEditor from "@/components/DynamicFormEditor/DynamicFormEditor.vue"
import BookingPreset from '@/models/BookingPreset.model'
import DynamicFormField from "@/models/DynamicFormField.model"
import NameBlock from "@/pages/Dashboard/Share/NameBlock.vue"
import mergeDefaultAndDataModel from "@/utils/mergeDefaultAndDataModel"
import PermissionChecker from "@/utils/PermissionChecker"
import { conditionalRule } from "@/utils/vuelidateHelper"
import BreadCrumb from "@/components/Dashboard/BreadCrumb";

export default {
  components: {
    AppDatePicker,
    AppTimePicker,
    AppDateTimePicker,
    OptionsEditor,
    StepNavigation,
    DynamicFormEditor,
    NameBlock,
    BreadCrumb
  },
  setup: () => ({ v$: useVuelidate() }),
  validations() {
    return {
      bookingPreset: {
        name: { required },
        type: { required },
        start_at: this.isEditing && this.step === '#publish' ? {
          required,
          beforeEndAt: () => {
            return !this.bookingPreset.start_at ||
              !this.bookingPreset.end_at ||
              isBefore(new Date(this.bookingPreset.start_at), new Date(this.bookingPreset.end_at))
          },
        } : {},
        end_at: this.isEditing && this.step === '#publish' ? {
          required,
        } : {},
        config: {
          booking_mapping: {
            bookings: {
              end_slot_minutes: {
                isPositiveInteger: conditionalRule({
                  when: () => this.isEditing && this.step === '#time-setting',
                  validator: (value) => Number.isInteger(parseInt(value)) && parseInt(value) >= 0,
                  message: '預留時間區段(分鐘)，必須為正整數',
                }),
              },
            }
          },
          basic: {
            lead_time: {
              amount_day: {
                isPositiveInteger: conditionalRule({
                  when: () => this.isEditing && this.step === '#time-setting' && this.bookingPreset.config?.basic?.lead_time?.type === 'day',
                  validator: (value) => Number.isInteger(parseInt(value)) && parseInt(value) > 0,
                  message: '提前預約設定輸入欄位必須為正整數',
                })
              },
              amount_minute: {
                isPositiveInteger: conditionalRule({
                  when: () => this.isEditing && this.step === '#time-setting' && this.bookingPreset.config?.basic?.lead_time?.type === 'minute',
                  validator: (value) => Number.isInteger(parseInt(value)) && parseInt(value) > 0,
                  message: '提前預約設定輸入欄位必須為正整數',
                })
              },
              lessThanBookingWindow: conditionalRule({
                when: () => this.isEditing && this.step === '#time-setting',
                validator: (value, vm) => (new BookingPreset(vm.bookingPreset)).bookingWindowIsGreaterThanLeadTime(),
                message: '提前預約設定不得超過預約時間限制',
              })
            }
          },
        },
      },
    };
  },
  computed: {
    consts() {
      return consts;
    },
    ...mapGetters("route", ["routeQuery"]),
    navigationSteps() {
      return [
        {
          title: "基本設定",
          hash: "",
          has_checkbox: true,
          is_checked: this.bookingPreset.id,
          is_disabled: false,
        },
        {
          title: "日期時段",
          hash: "#time-setting",
          has_checkbox: false,
          is_checked: false,
          is_disabled: !this.bookingPreset.id,
        },
        // {
        //   title: "分店時間表",
        //   hash: "#business-hours",
        //   has_checkbox: false,
        //   is_checked: false,
        //   is_disabled: !this.bookingPreset.id,
        // },
        {
          title: "配額設定",
          hash: "#limit-setting",
          has_checkbox: false,
          is_checked: false,
          is_disabled: !this.bookingPreset.id,
        },
        {
          title: "預約欄位",
          hash: "#fields",
          has_checkbox: true,
          is_checked: deepGet(this.bookingPreset, 'config.fields.length', 0) > 0,
          is_disabled: !this.bookingPreset.id,
        },
        {
          title: "發布",
          hash: "#publish",
          has_checkbox: false,
          is_checked: false,
          is_disabled: !this.bookingPreset.id,
        },
      ];
    },
    dataModel() {
      return new BookingPreset(this.bookingPreset)
    },
    statusConfigField() {
      return deepGet(this, "bookingPreset.config.fields", []).find((field) => field._id == this.dataModel.statusFieldId) ||
        this.defaultStatusConfigField
    },
    defaultStatusConfigField() {
      return (new DynamicFormField({
        type: 'select',
        title: '預約狀態',
        options: [
          { text: '顧客預約', value: '顧客預約' },
          { text: '接單確認', value: '接單確認' },
          { text: '服務完成', value: '服務完成' },
          { text: '顧客未到', value: '顧客未到' },
          { text: '顧客取消', value: '顧客取消' },
          { text: '店家取消', value: '店家取消' },
        ],
        dashboard_only: true,
      })).flattenedAttributes()
    },
    timeConfigField() {
      return deepGet(this, "bookingPreset.config.fields", []).find((field) => field._id == this.dataModel.startAtFieldId) ||
        this.defaultTimeConfigField
    },
    defaultTimeConfigField() {
      return (new DynamicFormField({
        type: 'datetime',
        title: '預約時間',
        format: 'datetime',
        date_condition: 'unlimited',
        date_style: 'calendar_style',
        options: [],
        time_condition: 'unlimited',
        time_style: 'time_select_style',
        time_options: [],
        start_time: null,
        end_time: null,
        slot_minutes: null,
      })).flattenedAttributes()
    },
    editableFields() {
      return deepGet(this, "bookingPreset.config.fields", [])
        .filter((field) => false === [this.dataModel.startAtFieldId, this.dataModel.statusFieldId].includes(field._id))
    },
    sortableFields() {
      return [
        this.statusConfigField,
        this.timeConfigField,
        ...this.editableFields,
      ]
    },
    permissionOptions() {
      return [
        { text: "需註冊", value: "registered" },
      ]
    },
    bookingPresetTypes() {
      const availableTypes = []
      const permissionChecker = new PermissionChecker()

      if (permissionChecker.check([consts.BOOKING_NORMAL_MODE])) {
        availableTypes.push({ text: "一般模式", value: "normal" })
      }
      if (permissionChecker.check([consts.BOOKING_WIZARD_MODE])) {
        availableTypes.push({ text: "精靈模式", value: "wizard" })
      }
      return availableTypes
    },
    hasBookingPresetTypePermission() {
      return this.bookingPresetTypes.length > 0
    },
  },
  data() {
    return {
      isEditing: false,
      showLoading: false,
      validationErrors: null,
      step: "",
      bookingPreset: { ...BookingPreset.defaultValue() },
      validSubject: false,
      reservationPeriods: {},
    };
  },
  mounted() {
    if (this.$route.name === "BookingPresetEditMinimal") {
      this.isEditing = true;
      this.bookingPreset.id = this.$route.params.id;
      this.fetchBookingPreset();
      this.$refs.stepNavigation.navigateToCurrentRoute();
    } else {
      this.isEditing = false;
      this.$set(
        this,
        "bookingPreset",
        this.mergeDefaultAndDataModel(this.bookingPreset, null)
      );

      this.bookingPreset.config.booking_mapping.bookings.status = this.defaultStatusConfigField._id
      this.bookingPreset.config.booking_mapping.bookings.start_at = this.defaultTimeConfigField._id

      this.bookingPreset.config.fields.push(this.defaultStatusConfigField)
      this.bookingPreset.config.fields.push(this.defaultTimeConfigField)

      // 權限只有一個時自動選取
      if (this.bookingPresetTypes.length === 1) {
        this.bookingPreset.type = this.bookingPresetTypes[0].value;
      }
    }

    const startTime = '09:00'
    const endTime = '18:00'
    const closingTime = '20:00'

    this.reservationPeriods = {
      "1": {
        "display_text": "星期一",
        "closing_time": closingTime,
        "is_official_holiday": false,
        "business_hours": [
          {
            "start_time": startTime,
            "end_time": endTime
          }
        ]
      },
      "2": {
        "display_text": "星期二",
        "closing_time": closingTime,
        "is_official_holiday": false,
        "business_hours": [
          {
            "start_time": startTime,
            "end_time": endTime
          }
        ]
      },
      "3": {
        "display_text": "星期三",
        "closing_time": closingTime,
        "is_official_holiday": false,
        "business_hours": [
          {
            "start_time": startTime,
            "end_time": endTime
          }
        ]
      },
      "4": {
        "display_text": "星期四",
        "closing_time": closingTime,
        "is_official_holiday": false,
        "business_hours": [
          {
            "start_time": startTime,
            "end_time": endTime
          }
        ]
      },
      "5": {
        "display_text": "星期五",
        "closing_time": closingTime,
        "is_official_holiday": false,
        "business_hours": [
          {
            "start_time": startTime,
            "end_time": endTime
          }
        ]
      },
      "6": {
        "display_text": "星期六",
        "closing_time": closingTime,
        "is_official_holiday": false,
        "business_hours": [
          {
            "start_time": startTime,
            "end_time": endTime
          }
        ]
      },
      "7": {
        "display_text": "星期日",
        "closing_time": closingTime,
        "is_official_holiday": false,
        "business_hours": [
          {
            "start_time": startTime,
            "end_time": endTime
          }
        ]
      }
    }
  },
  watch: {
    'bookingPreset.type'(newType) {
      if (newType === 'normal') {
        this.bookingPreset.config.occupy_config.is_check_service = false;
        this.bookingPreset.config.occupy_config.is_check_staff = false;
      }
    }
  },
  methods: {
    subjectIsFixed(subject) {
      return [this.dataModel.statusFieldId, this.dataModel.startAtFieldId].includes(subject._id)
    },
    deepGet,
    mergeDefaultAndDataModel,
    presentValidationErrors(validationKey) {
      return deepGet(this.v$, validationKey + ".$errors", [])
        .map((error) => error.$message)
        .join(", ");
    },
    cancel() {
      this.$router.push({ name: "BookingPresetList", query: this.routeQuery });
    },
    async submit() {
      const result = await this.v$.$validate();
      if (!result) {
        if (this.step !== '') {

          if (['amount_minute', 'amount_day'].includes(this.v$.$errors[0].$property) || this.v$.$errors[0].$validator === 'lessThanBookingWindow') {
            this.$swal({
              type: 'error',
              title: '欄位填寫有誤',
              html: this.v$.$errors
                .map(error => `${error.$message}`)
                .join('<br/>'),
              confirmButtonText: '確認',
            })
          } else {
            this.$swal({
              type: 'error',
              title: '欄位填寫有誤',
              html: this.v$.$errors
                .map(error => `${error.$propertyPath}: ${error.$message}`)
                .join('<br/>'),
              confirmButtonText: '確認',
            })
          }
        }
        return false;
      }

      try {
        this.dataModel.validateNormalConfiguration()
      } catch (error) {
        this.$swal({
          type: 'error',
          title: '設定有誤',
          html: error.messages.join('<br/>'),
          confirmButtonText: '確認',
        })
        return false
      }

      try {
        this.showLoading = true;

        const payload = this.bookingPreset;

        // 編輯表單
        if (this.isEditing) {
          await bookingApi.updateBookingPreset(
            this.bookingPreset.id,
            payload
          );
          await this.fetchBookingPreset();
          this.$swal("儲存成功", "", "success");
        }
        // 新增表單
        else {
          const response = await bookingApi.createBookingPreset(payload);
          this.bookingPreset = response.data.data;
          this.$swal("新增成功", "", "success");
          this.isEditing = true;
          this.$router.replace({
            name: "BookingPresetEditMinimal",
            params: { id: response.data.data.id },
          });
        }

        return true;
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "儲存失敗", "error");
        return false;
      } finally {
        this.showLoading = false;
      }
    },
    async fetchBookingPreset() {
      if (!this.bookingPreset.id) {
        this.$swal("錯誤", "無法取得讀取 ID", "error");
        return;
      }

      try {
        this.showLoading = true;
        const response = await bookingApi.getBookingPreset(this.bookingPreset.id);

        this.$set(
          this,
          "bookingPreset",
          this.mergeDefaultAndDataModel({ ...BookingPreset.defaultValue() }, response.data.data)
        );
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "讀取資料錯誤", "error");
        this.cancel();
      } finally {
        this.showLoading = false;
      }
    },
    async saveFields(fields) {
      this.validSubject = true

      if (fields.some(subject => !subject.title)) {
        return;
      }

      try {
        this.showLoading = true;

        this.bookingPreset.config.fields = fields;

        await bookingApi.updateBookingPreset(
          this.bookingPreset.id,
          this.bookingPreset
        );
        await this.fetchBookingPreset();
        this.$swal("儲存成功", "", "success");
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "儲存資料錯誤", "error");
      } finally {
        this.showLoading = false;
      }
    },
    updateReservationPeriod(dayIndex, field, value) {
      if (this.reservationPeriods[dayIndex]) {
        if (field === 'start_time' || field === 'end_time') {
          this.reservationPeriods[dayIndex].business_hours[0][field] = value
        } else {
          this.reservationPeriods[dayIndex][field] = value
        }
      }
    },
    handleOccupanceLimitTypeChange(value) {
      this.bookingPreset.config.occupy_config.enabled = value !== 'unlimited'
    },
  },
};
</script>

<style lang="scss" scoped>
.reservation-periods-container {
  .reservation-periods-header {
    font-weight: bold;
    margin-bottom: 10px;
    padding-bottom: 5px;
    border-bottom: 1px solid #dee2e6;
  }

  .reservation-period-row {
    padding: 8px 0;

    &:hover {
      background-color: #f8f9fa;
    }
  }
}
</style>
